/**
 * VoiceWelcomeCard — Stage 11 voice discovery nudge.
 *
 * One-time warm welcome shown in the chat landing view. Autoplays the first
 * sentence aloud so the user hears Kindra's voice in their first 10 seconds —
 * the audit's "wow moment that sells subscriptions" pattern.
 *
 * Shown only when:
 *   • Browser supports SpeechSynthesis (useTextToSpeech reports supported).
 *   • User has not dismissed or seen the card before (localStorage flag).
 *   • User is on the chat landing (session list) view — not inside a session.
 *
 * Tracking:
 *   • `voice_welcome_shown`    — card rendered
 *   • `voice_welcome_played`   — user heard the autoplay or manually tapped play
 *   • `voice_welcome_dismissed`— explicit tap on dismiss or "Got it"
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SpeakerHigh, X } from '@phosphor-icons/react';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { track as trackEvent } from '../../services/activation';

const SEEN_KEY = 'kindra_voice_welcome_seen_v1';
const WELCOME_TEXT = "Hi, I'm Kindra. You can type or just talk to me.";

export const VoiceWelcomeCard = ({ hasExistingChats = false }) => {
  const tts = useTextToSpeech();
  const [dismissed, setDismissed] = useState(() => {
    try {
      // Stage 20B+ Wave B — auto-dismiss on users with any existing session
      // history. The card was designed for first-time users; showing it to
      // returning users steals real-estate every time they open /chat.
      if (hasExistingChats) {
        try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* noop */ }
        return true;
      }
      return localStorage.getItem(SEEN_KEY) === '1';
    } catch { return false; }
  });
  const autoplayFiredRef = useRef(false);

  // Stage 20B+ Wave B — the sessions list arrives from an async fetch, so
  // `hasExistingChats` is false on first mount and may flip true seconds
  // later. React to that transition: if the user turns out to have history,
  // dismiss immediately, persist the flag so we never show it again, and
  // stop any autoplay currently in flight.
  useEffect(() => {
    if (hasExistingChats && !dismissed) {
      try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* noop */ }
      setDismissed(true);
      tts.stop?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExistingChats]);

  // Fire "shown" analytics exactly once.
  useEffect(() => {
    if (dismissed || !tts.supported) return;
    trackEvent('voice_welcome_shown', { surface: 'chat_landing' });
  }, [dismissed, tts.supported]);

  // Autoplay the welcome sentence ONCE on first mount (after a short delay so
  // the synth voice list has time to load on Safari).
  useEffect(() => {
    if (dismissed || !tts.supported) return;
    if (autoplayFiredRef.current) return;
    autoplayFiredRef.current = true;
    const t = setTimeout(() => {
      tts.play(WELCOME_TEXT);
      trackEvent('voice_welcome_played', { kind: 'auto' });
    }, 900);
    return () => clearTimeout(t);
  }, [dismissed, tts.supported]);

  const handleDismiss = (kind) => {
    trackEvent('voice_welcome_dismissed', { kind });
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
    tts.stop();
  };

  const handleReplay = () => {
    tts.play(WELCOME_TEXT);
    trackEvent('voice_welcome_played', { kind: 'replay' });
  };

  if (dismissed || !tts.supported) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="voice-welcome"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.35 }}
        className="relative rounded-2xl border border-[#2FB3A0]/30 bg-gradient-to-br from-[#2FB3A0]/[0.06] to-white px-4 py-4"
        data-testid="voice-welcome-card"
      >
        <button
          type="button"
          onClick={() => handleDismiss('x')}
          className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center text-slate-300 hover:text-slate-500 rounded-full"
          aria-label="Dismiss"
          data-testid="voice-welcome-dismiss"
        >
          <X size={12} weight="bold" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <button
            type="button"
            onClick={handleReplay}
            className={`flex-shrink-0 w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors ${
              tts.speaking && !tts.paused
                ? 'bg-[#2FB3A0] text-white'
                : 'bg-white border border-[#2FB3A0]/40 text-[#2FB3A0] hover:bg-[#2FB3A0] hover:text-white'
            }`}
            aria-label="Play Kindra's voice"
            data-testid="voice-welcome-play"
          >
            <SpeakerHigh size={16} weight="duotone" />
            {tts.speaking && !tts.paused && (
              <span className="absolute inset-0 rounded-full bg-[#2FB3A0]/30 animate-ping pointer-events-none" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#2FB3A0] mb-1">Voice mode · new</p>
            <p
              className="font-serif text-[17px] leading-snug text-[#0E1C2E]"
              data-testid="voice-welcome-text"
            >
              {WELCOME_TEXT}
            </p>
            <p className="text-[12px] text-slate-500 mt-1.5 leading-relaxed">
              Tap the microphone to speak. Tap any reply's speaker to hear it read aloud.
            </p>
            <button
              type="button"
              onClick={() => handleDismiss('got_it')}
              className="mt-2 text-[12px] font-semibold text-[#0E1C2E] hover:text-[#1A2B42] inline-flex items-center gap-1"
              data-testid="voice-welcome-got-it"
            >
              Got it →
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VoiceWelcomeCard;
